import { useEffect, useState } from 'react';
import { customerApi } from '../customer_api';
import { useLocale, useT } from '../i18n/i18n';
import { LOCALE_LABELS, type Locale, isSupportedLocale } from '../i18n/locales';
import {
  currentPushSubscription,
  getPushPermission,
  registerPushSubscription,
  unregisterPushSubscription,
} from '../pwa';

interface ProfileData {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  locale: string | null;
  accountSlug: string;
}

export function Profile() {
  const t = useT();
  const { locale: activeLocale, setLocale, supportedLocales } = useLocale();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  useEffect(() => {
    customerApi<ProfileData>('/api/customer/profile')
      .then((data) => {
        setProfile(data);
        setName(data.name ?? '');
        setPhone(data.phone ?? '');
      })
      .catch(() => setLoadError(t('profile.load_failed')));
  }, [t]);

  // Locale picker change handler — applies the new locale immediately to the
  // app shell (re-renders all useT() consumers) AND fires a PATCH so the
  // preference persists across sessions. We do NOT call setProfile here —
  // the inner form state is independent of the locale field, which is
  // rendered from `activeLocale` (the live React context value).
  const handleLocaleChange = (next: string) => {
    if (!isSupportedLocale(next)) return;
    setLocale(next as Locale);
    // Persist via the PATCH endpoint. The CustomerLocaleProvider also wires
    // this PATCH for changes that originate from its own state, but the
    // picker calls setLocale directly on the inner LocaleProvider —
    // CustomerLocaleProvider's onLocaleChange handles the persist.
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true);
    setSaveError(null);
    setSavedOk(false);
    try {
      const updated = await customerApi<ProfileData>('/api/customer/profile', {
        method: 'PATCH',
        body: JSON.stringify({ name: name || null, phone: phone || null }),
      });
      setProfile(updated);
      setName(updated.name ?? '');
      setPhone(updated.phone ?? '');
      setSavedOk(true);
    } catch {
      setSaveError(t('profile.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.55rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid var(--border, oklch(0.915 0.005 80))',
    background: 'var(--surface, white)',
    fontSize: '0.9rem',
    color: 'var(--foreground, oklch(0.19 0.012 75))',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 500,
    marginBottom: '0.35rem',
    color: 'var(--foreground, oklch(0.19 0.012 75))',
  };

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 1.5rem',
          }}
        >
          Profile
        </h1>

        {loadError && (
          <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{loadError}</p>
        )}

        {!loadError && profile === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {profile !== null && (
          <form
            onSubmit={handleSave}
            style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}
          >
            <div>
              <label htmlFor="profile-name" style={labelStyle}>
                Name
              </label>
              <input
                id="profile-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="profile-phone" style={labelStyle}>
                Phone
              </label>
              <input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={inputStyle}
                placeholder="+44 7700 900123"
              />
            </div>

            <div>
              <label htmlFor="profile-email" style={labelStyle}>
                Email
              </label>
              <input
                id="profile-email"
                type="email"
                value={profile.email}
                disabled
                style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
              />
              <p
                style={{
                  margin: '0.35rem 0 0',
                  fontSize: '0.78rem',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                Contact us to change your email.
              </p>
            </div>

            <div>
              <label htmlFor="profile-locale" style={labelStyle}>
                {t('profile.language_label')}
              </label>
              <select
                id="profile-locale"
                value={activeLocale}
                onChange={(e) => handleLocaleChange(e.target.value)}
                style={inputStyle}
              >
                {supportedLocales.map((loc) => (
                  <option key={loc} value={loc}>
                    {LOCALE_LABELS[loc]}
                  </option>
                ))}
              </select>
              <p
                style={{
                  margin: '0.35rem 0 0',
                  fontSize: '0.78rem',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                {t('profile.language_help')}
              </p>
            </div>

            <div
              style={{
                fontSize: '0.78rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              {t('profile.account_label')} <span className="num">{profile.accountSlug}</span>
            </div>

            {saveError && (
              <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))', margin: 0 }}>
                {saveError}
              </p>
            )}

            {savedOk && (
              <p
                style={{
                  color: 'var(--success, oklch(0.52 0.105 150))',
                  margin: 0,
                  fontSize: '0.875rem',
                }}
              >
                Changes saved.
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '0.6rem 1.25rem',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--primary, oklch(0.43 0.18 263))',
                color: 'var(--primary-foreground, oklch(0.985 0.003 85))',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.65 : 1,
                alignSelf: 'flex-start',
              }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        )}

        <PushNotificationToggle />
      </div>
    </main>
  );
}

/**
 * Per-device push notification toggle. Reads the current subscription state
 * on mount and exposes Enable / Disable that round-trip through the
 * /api/customer/device-tokens server-side registration (sub-spec 12 Plan 2).
 *
 * Disable only revokes locally — the server-side device_tokens row stays
 * (idempotent re-subscribe is the usual flow). Customers wanting full
 * suppression toggle the per-channel preference at /account/notifications.
 */
function PushNotificationToggle() {
  const [perm, setPerm] = useState<ReturnType<typeof getPushPermission>>(() => getPushPermission());
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPerm(getPushPermission());
    void currentPushSubscription().then((sub) => setSubscribed(!!sub));
  }, []);

  if (perm === 'unsupported') {
    return (
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Push notifications</h2>
        <p style={sectionBodyStyle}>
          Your browser does not support push notifications. Install the app on a supported device to
          enable them.
        </p>
      </section>
    );
  }

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await registerPushSubscription();
      if (res.ok) {
        setPerm('granted');
        setSubscribed(true);
      } else if (res.reason === 'denied') {
        setError(
          'Notifications are blocked in your browser. Open the lock icon by the URL bar to allow them.',
        );
        setPerm('denied');
      } else if (res.reason === 'unsupported') {
        setError('Your browser does not support push notifications.');
        setPerm('unsupported');
      } else {
        setError('Could not enable notifications. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await unregisterPushSubscription();
      setSubscribed(false);
    } catch {
      setError('Could not disable notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>Push notifications</h2>
      <p style={sectionBodyStyle}>
        {subscribed
          ? 'This device is set up to receive push notifications.'
          : 'Enable push notifications on this device for stay updates, pickup reminders, and photos.'}
      </p>
      {error && (
        <p
          style={{
            margin: '0.5rem 0 0',
            fontSize: '0.8rem',
            color: 'var(--destructive, oklch(0.55 0.15 30))',
          }}
        >
          {error}
        </p>
      )}
      <div style={{ marginTop: '0.85rem' }}>
        {subscribed ? (
          <button
            type="button"
            onClick={handleDisable}
            disabled={busy}
            style={{
              padding: '0.5rem 0.95rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Disabling…' : 'Disable on this device'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy || perm === 'denied'}
            style={{
              padding: '0.5rem 0.95rem',
              borderRadius: '8px',
              border: 'none',
              background:
                perm === 'denied'
                  ? 'var(--muted, oklch(0.92 0.005 80))'
                  : 'var(--primary, oklch(0.43 0.18 263))',
              color: perm === 'denied' ? 'var(--muted-foreground, oklch(0.52 0.008 75))' : 'white',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: busy || perm === 'denied' ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Enabling…' : perm === 'denied' ? 'Blocked in browser' : 'Enable'}
          </button>
        )}
      </div>
    </section>
  );
}

const sectionStyle: React.CSSProperties = {
  marginTop: '2rem',
  padding: '1.1rem 1.25rem',
  border: '1px solid var(--border, oklch(0.915 0.005 80))',
  borderRadius: '14px',
  background: 'var(--surface, white)',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 0.4rem',
  fontSize: '0.95rem',
  fontWeight: 600,
};

const sectionBodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.85rem',
  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
  lineHeight: 1.5,
};
